import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import AttemptReview from "./AttemptReview";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  Loader2, X, CheckCircle2, Clock, AlertTriangle, Camera, ClipboardCheck, Users, Sparkles,
} from "lucide-react";

/**
 * One student, and everything that has been set for them.
 *
 * The gap this fills: the Students screen knew who had signed up and showed
 * their multiple-choice quiz scores, while the actual homework was only
 * visible from the other direction - open an assignment, find the child's name
 * in the list. Asked "how is Amaya getting on", the portal had no page that
 * answered it.
 *
 * Deliberately no cohort average on this panel even though a tutor could
 * handle one. A tutor opening a child's record is about to talk to the child
 * or the parent, and a rank is the wrong number for that conversation. The
 * comparison lives on the Progress screen, which is about the group.
 */

function Kpi({ label, value, sub, tone = "slate", icon: Icon }) {
  const tones = {
    slate: "text-slate-900", green: "text-green-700", amber: "text-amber-700", red: "text-red-700",
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3.5 py-3">
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        {Icon && <Icon size={12} className="text-slate-400" />} {label}
      </div>
      <div className={`text-xl font-bold mt-0.5 ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function TopicBars({ topics }) {
  if (!topics.length) {
    return <p className="text-sm text-slate-400 py-6 text-center">
      Nothing marked yet, so there are no topic figures.
    </p>;
  }
  return (
    <div className="space-y-2.5">
      {topics.map((t) => {
        const colour = t.pct == null ? "#cbd5e1" : t.pct >= 80 ? "#15803d" : t.pct >= 60 ? "#a16207" : "#b91c1c";
        return (
          <div key={t.topic}>
            <div className="flex justify-between items-baseline text-xs">
              <span className="font-medium text-slate-700 truncate pr-2">{t.topic}</span>
              <span className="font-bold shrink-0" style={{ color: colour }}>
                {t.pct == null ? "—" : `${t.pct}%`}
              </span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden mt-1">
              <div className="h-full rounded-full" style={{ width: `${t.pct || 0}%`, background: colour }} />
            </div>
            <div className="text-[10px] text-slate-400 mt-0.5">
              {t.awarded} of {t.available} marks · {t.marked} of {t.questions} question{t.questions === 1 ? "" : "s"} marked
              {t.source === "self" ? " · their own marking" : ""}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function StudentWork({ studentId, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [openAttempt, setOpenAttempt] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/students/${studentId}/work`)
      .then((r) => setD(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Could not load that student's work"));
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const s = d?.summary;
  const rows = d?.assignments || [];

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl my-6 shadow-xl">
        <div className="flex items-start justify-between gap-3 px-5 py-3.5 border-b border-slate-200">
          <div className="min-w-0">
            <h2 className="font-semibold text-slate-800 truncate">
              {d?.student?.name || "Student"}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {[
                d?.student?.year ? `Year ${d.student.year}` : null,
                d?.student?.tier,
                d?.student?.board,
                (d?.groups || []).map((g) => g.name).join(", ") || null,
              ].filter(Boolean).join(" · ") || "—"}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 shrink-0"><X size={18} /></button>
        </div>

        <div className="p-5">
          {err && <p className="text-sm text-red-700">{err}</p>}
          {!d && !err && (
            <p className="flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 size={15} className="animate-spin" /> Loading…
            </p>
          )}

          {d && s && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                <Kpi label="Set for them" value={s.assigned} icon={ClipboardCheck}
                     sub={s.assigned === 0 ? "nothing yet" : null} />
                <Kpi label="Handed in" value={s.handed_in} tone="green" icon={CheckCircle2}
                     sub={`${s.outstanding} still to do`} />
                <Kpi label="Overdue" value={s.overdue} tone={s.overdue ? "red" : "slate"} icon={AlertTriangle} />
                <Kpi label="Marks earned" value={s.marked.pct == null ? "—" : `${s.marked.pct}%`}
                     tone={s.marked.pct == null ? "slate" : s.marked.pct >= 60 ? "green" : "amber"}
                     sub={s.marked.available
                       ? `${s.marked.awarded} of ${s.marked.available}${s.auto.available ? ` · ${s.auto.awarded} checked automatically` : ""}`
                       : "nothing marked yet"} />
                <Kpi label="Photos of working" value={s.photos} icon={Camera} />
              </div>

              {s.auto.confirmed > 0 && (
                <p className="mt-3 text-xs text-slate-500 inline-flex items-center gap-1.5">
                  <Sparkles size={12} className="text-slate-400" />
                  <span>
                    <strong className="text-slate-700">{s.auto.confirmed}</strong> marks were confirmed
                    automatically &mdash; the typed answer matched the mark scheme exactly. Anything the
                    checker could not confirm is left for you, never marked wrong.
                  </span>
                </p>
              )}

              {s.self.pct != null && (
                <p className="mt-3 text-xs text-slate-500">
                  They marked themselves <strong className="text-slate-700">{s.self.pct}%</strong>
                  {" "}({s.self.awarded} of {s.self.available}) on the questions you have not marked.
                  Their figure and yours are kept apart on purpose.
                </p>
              )}

              {s.history.length > 1 && (
                <div className="mt-5">
                  <h3 className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-2">
                    Their own trend — each worksheet, in order
                  </h3>
                  <div className="h-[180px] rounded-xl border border-slate-200 bg-white p-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={s.history} margin={{ top: 8, right: 12, bottom: 4, left: -18 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#94a3b8" }} />
                        <Tooltip
                          contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
                          formatter={(v, _n, p) => [`${v}% (${p.payload.awarded} of ${p.payload.available})`, p.payload.title]}
                        />
                        <Line type="monotone" dataKey="pct" stroke="#1e293b" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              <div className="grid lg:grid-cols-2 gap-5 mt-5">
                <div>
                  <h3 className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-2">
                    Topics, weakest first
                  </h3>
                  <TopicBars topics={s.topics} />
                </div>

                <div>
                  <h3 className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-2">
                    What they have been given ({rows.length})
                  </h3>
                  <div className="border border-slate-200 rounded-xl divide-y divide-slate-100">
                    {rows.map((r) => (
                      <button key={r.attempt_id} onClick={() => setOpenAttempt(r.attempt_id)}
                        className="w-full text-left px-3.5 py-3 hover:bg-slate-50/70">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <span className="font-medium text-sm text-slate-900 flex-1 min-w-[120px] truncate">
                            {r.title}
                          </span>
                          {r.status === "submitted"
                            ? <span className="text-xs text-green-700 inline-flex items-center gap-1"><CheckCircle2 size={12} /> handed in</span>
                            : <span className="text-xs text-amber-700 inline-flex items-center gap-1"><Clock size={12} /> to do</span>}
                          <span className="text-xs font-semibold text-slate-700">
                            {r.tutor.pct != null
                              ? `${r.tutor.awarded}/${r.tutor.available}`
                              : r.auto.pct != null ? `auto ${r.auto.awarded}/${r.auto.available}`
                              : r.self.pct != null ? `self ${r.self.awarded}/${r.self.available}` : "unmarked"}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-400 mt-0.5 capitalize">
                          {r.kind}
                          {r.group_name ? ` · ${r.group_name}` : ""}
                          {r.due_date ? ` · due ${r.due_date}` : ""}
                          {" · "}{r.question_count} questions
                          {" · "}{r.photos_total} photo{r.photos_total === 1 ? "" : "s"}
                          {" · "}{r.tutor.marked} of {r.question_count} marked
                        </div>
                      </button>
                    ))}
                    {rows.length === 0 && (
                      <p className="px-3.5 py-8 text-sm text-slate-500 text-center">
                        Nothing has been set for this student yet. Give out a worksheet, or add them to a group.
                      </p>
                    )}
                  </div>
                  {(d.groups || []).length > 0 && (
                    <p className="mt-2 text-[11px] text-slate-400 flex items-center gap-1">
                      <Users size={11} /> In {d.groups.map((g) => g.name + (g.academic_year ? ` (${g.academic_year})` : "")).join(", ")}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {openAttempt && (
        <AttemptReview attemptId={openAttempt} onClose={() => setOpenAttempt(null)} onChanged={load} />
      )}
    </div>
  );
}
